
import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Panel } from "@/hooks/use-panel-detection";
import { ArrowLeft, ArrowRight, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface PanelReaderProps {
  panels: Panel[];
  images: string[];
  currentPanelIndex: number | null;
  zoomMode: boolean;
  onPanelClick: (panelIndex: number) => void;
  onExitZoom: () => void;
  onNextPanel: () => void;
  onPrevPanel: () => void;
}

export function PanelReader({
  panels,
  images,
  currentPanelIndex,
  zoomMode,
  onPanelClick,
  onExitZoom,
  onNextPanel,
  onPrevPanel
}: PanelReaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [imageDimensions, setImageDimensions] = useState<Record<number, { width: number, height: number }>>({});

  // Load image dimensions
  useEffect(() => {
    images.forEach((src, index) => {
      const img = new Image();
      img.onload = () => {
        setImageDimensions(prev => ({
          ...prev,
          [index]: { width: img.width, height: img.height }
        }));
      };
      img.src = src;
    });
  }, [images]);

  // Set up keyboard navigation
  useEffect(() => {
    if (!zoomMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case " ": // Space
          onNextPanel();
          break;
        case "ArrowLeft":
          onPrevPanel();
          break;
        case "Escape":
          onExitZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomMode, onNextPanel, onPrevPanel, onExitZoom]);

  // Touch controls for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    
    const deltaX = touchEndX - touchStartX;
    const deltaY = touchEndY - touchStartY;
    
    // Detect horizontal swipe (if horizontal movement is greater than vertical)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Check if the swipe was long enough
      if (Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          onPrevPanel(); // Right swipe
        } else {
          onNextPanel(); // Left swipe
        }
      }
    }
    
    setTouchStartX(null);
    setTouchStartY(null);
  };

  // Handle panel click
  const handlePanelClick = (panelIndex: number) => {
    if (!zoomMode) {
      onPanelClick(panelIndex);
    }
  };

  // Show reading help toast when entering zoom mode
  useEffect(() => {
    if (zoomMode && currentPanelIndex !== null) {
      toast({
        title: "Modo de leitura ativado",
        description: "Use as setas ou toque nas bordas para navegar",
        duration: 3000
      });
    }
  }, [zoomMode, currentPanelIndex]);

  const getCurrentPanel = () => {
    if (currentPanelIndex === null || !panels[currentPanelIndex]) return null;
    return panels[currentPanelIndex];
  };

  const currentPanel = getCurrentPanel();

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Full episode view (when not in zoom mode) */}
      <div className={`transition-opacity duration-300 ${zoomMode ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        {images.map((image, imageIndex) => (
          <div key={imageIndex} className="w-full flex justify-center mb-1 relative">
            <img 
              src={image} 
              alt={`Image ${imageIndex + 1}`} 
              className="w-full max-w-screen-lg object-contain"
              loading="lazy"
            />
            
            {/* Overlay panels for click detection */}
            {panels
              .filter(panel => panel.imageIndex === imageIndex)
              .map(panel => {
                const imageWidth = imageDimensions[imageIndex]?.width || 0;
                
                return (
                  <div
                    key={panel.id}
                    className="absolute cursor-pointer hover:bg-white/10 hover:border-2 hover:border-primary/40 transition-all duration-200"
                    style={{
                      left: imageWidth ? `${(panel.x / imageWidth) * 100}%` : '0',
                      top: `${panel.y}px`,
                      width: `${panel.width}px`,
                      height: `${panel.height}px`
                    }}
                    onClick={() => handlePanelClick(panel.id)}
                  />
                );
              })}
          </div>
        ))}
      </div>

      {/* Panel zoom view */}
      <AnimatePresence>
        {zoomMode && currentPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative max-w-full max-h-full overflow-hidden"
              >
                {/* Fix: Display the full panel image instead of using clip-path */}
                <div className="relative max-h-[90vh] max-w-[90vw]">
                  <img
                    src={images[currentPanel.imageIndex]}
                    alt={`Panel ${currentPanel.id}`}
                    className="object-contain max-h-[90vh] max-w-full"
                  />
                  <div
                    className="absolute border-4 border-primary/50 pointer-events-none"
                    style={{
                      top: `${currentPanel.y}px`,
                      left: `${currentPanel.x}px`,
                      width: `${currentPanel.width}px`,
                      height: `${currentPanel.height}px`,
                    }}
                  />
                </div>
              </motion.div>
            </div>

            {/* Navigation controls */}
            <div className="absolute inset-x-0 top-4 flex justify-center">
              <Button variant="ghost" size="icon" onClick={onExitZoom} className="bg-black/50 hover:bg-black/70">
                <Minimize2 className="h-5 w-5" />
              </Button>
            </div>

            <div className="absolute inset-y-0 left-0 flex items-center">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onPrevPanel}
                className="h-16 w-12 bg-black/30 hover:bg-black/50 rounded-r-lg"
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>
            </div>

            <div className="absolute inset-y-0 right-0 flex items-center">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onNextPanel}
                className="h-16 w-12 bg-black/30 hover:bg-black/50 rounded-l-lg"
              >
                <ArrowRight className="h-6 w-6" />
              </Button>
            </div>

            <div className="absolute inset-x-0 bottom-4 flex justify-center">
              <div className="bg-black/50 px-3 py-1 rounded-full text-xs">
                Painel {currentPanelIndex !== null ? currentPanelIndex + 1 : 0} de {panels.length}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-page view toggle button (only visible when not in zoom mode) */}
      {!zoomMode && panels.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="fixed bottom-4 right-4 bg-black/50 hover:bg-black/70 z-10"
          onClick={() => onPanelClick(0)}
        >
          <Maximize2 className="h-4 w-4 mr-2" />
          Modo Leitura
        </Button>
      )}
    </div>
  );
}
